import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { AppStateService, HttpService, EnvService, PROJECT_CONSTANTS, TokenService, CsrfTokenService, DataService, AuthenticationService } from '../../../../../../usermgmt/src/public-api';
import { isNullOrUndefined } from '../../../../../../usermgmt/src/lib/shared/common-utils';
import { hasApplicationAccess, parseUserPermissions } from '../../../../../../usermgmt/src/lib/shared/permission.util';
import { KeycloakService } from '../../../../../../usermgmt/src/lib/services/keycloak.service';
import { isPlatformBrowser } from '@angular/common';
import { MessageService } from 'primeng/api';

// Map resources to route paths
const RESOURCE_ROUTE_MAP: { [key: string]: string } = {
  'Geo-Portal': '/home',
  'Unauthorized Cultivation': '/unauthorized-cultivation',
  'Metadata Management': '/metadata-management',
  'Unauthorized Construction': '/unauthorized-construction',
  'Green Cover Monitoring': '/green-cover-monitoring',
  'Road Condition Monitoring': '/road-network-monitoring',
  'Layout Dashboard': '/layout',
  'User Management': '/usermgmt',
  'IFC Viewer': '/ifc',
  'New Geoportal': '/new-geoportal',
  'Land Acquisitions': '/land-acquisitions',
  'Infra Zone': '/infra-zone',
  'Shelter Management' : '/shelter-management',
  'Rainfall' : '/rainfall',
  'Wind' : '/winds',
  'Lightning' : '/lightning',
  'Flooding' : '/flooding',
  'Financial Overview' : '/financial',
  'Estate Management' : '/estate-management',
  'Heatwave Advisory' : '/heatmap',
  'Works Module' : '/works',
  'Cyclone' : '/cyclone',
  'Land Acquisition And NSP' : '/land-acquisition-nsp-overview',
  'Sector Zone': '/sector-zone',
  'LPS Village View': '/lps-village'
};


@Injectable({
  providedIn: 'root'
})
export class ApplicationGuard implements CanActivate {
  userPermissions: any;
  private isBrowser: boolean;

  constructor(
    private authSrv: AuthenticationService,
    private keycloak: KeycloakService,
    private router: Router,
    private appStateSrv: AppStateService,
    private httpSrv: HttpService,
    private envService: EnvService,
    // private utilService: UtilsService,
    private messageService: MessageService,
    private tokenService: TokenService,
    private csrfTokenSrv: CsrfTokenService,
    private dataSrv: DataService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.checkAccess(next, state);
  }

  private async checkAccess(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    const resource = next.data['resource'] || '';
    const loggedIn = this.authSrv.isLoggedIn() || this.keycloak.isLoggedIn();

    console.log("loggedIn==", loggedIn,resource)

    if (!loggedIn) {
      if (this.isBrowser && typeof window !== 'undefined') {
        this.keycloak.login({ redirectUri: window.location.origin + '/landing' });
      }
      return false;
    }

    const localRefreshToken = this.isBrowser && typeof localStorage !== 'undefined' ? localStorage.getItem('refreshToken') : null;

    if (localRefreshToken && this.keycloak.isRefreshTokenExpired()) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Your session is expired' });
      setTimeout(() => {
        this.keycloak.logout();
      }, 200);
      return false;
    }

    const accessToken = await this.keycloak.getToken();
    if (this.isBrowser && typeof localStorage !== 'undefined') {
      localStorage.setItem('token', accessToken);
    }

    if (!accessToken || this.tokenService.isTokenExpired(accessToken)) {
      return this.handleUnAuthorizedAccess("Session expired. Please log in again.", ['username']);
    }

    const tokenValidated = await this.validateCsrfToken(accessToken);

    if (!tokenValidated) {
      return this.handleUnAuthorizedAccess("Please login to view requested page.", ['username']);
    }
    let userId;
    if (this.keycloak.isLoggedIn()) {
      const userProfile = await this.appStateSrv.getUserInfo();
      userId = userProfile.id;
    } else {
      userId = this.appStateSrv.getUserInfoFromLocalStorage()?.userId;
    }

    if (!userId) {
      this.keycloak.logout();
      return false;
    }

    const hasPermissionsLoaded = this.appStateSrv.getPermissionsLoaded();
    this.userPermissions = this.appStateSrv.getUserPermissions();

    const isLanding = next.routeConfig?.path === 'landing';

    if (hasPermissionsLoaded) {
      return this.handlePermissionsLoaded(resource, isLanding);
    } else {
      return this.loadUserPermissions(userId, resource, isLanding);
    }
  }

  private handlePermissionsLoaded(resource: string, isLanding: boolean = false): boolean | UrlTree {
    if (isLanding) {
      return true;
    }

    const permissions = parseUserPermissions(this.userPermissions);
    const hasApcrdaAccess = hasApplicationAccess(permissions, PROJECT_CONSTANTS.APPLICATION_NAME);
    const hasUmAccess = hasApplicationAccess(permissions, PROJECT_CONSTANTS.USERMGMT_APP_NAME);

    if (resource === PROJECT_CONSTANTS.USERMGMT_APP_NAME) {
      return hasUmAccess ? true : this.handleUnAuthorizedAccess();
    }

    if (hasApcrdaAccess) {
      return this.checkPermissions(resource);
    }

    if (hasUmAccess) {
      this.router.navigate(['usermgmt', 'applications']);
      return false;
    }

    return this.handleUnAuthorizedAccess();
  }

  private async validateCsrfToken(accessToken: string): Promise<boolean> {
    if (!isNullOrUndefined(accessToken)) {
      if (!this.csrfTokenSrv.doesCsrfTokenExist()) {
        const response = await this.csrfTokenSrv.generateCsrfToken(accessToken);
        if (Object.keys(response).length === 0) {
          return false;
        }
        this.setCsrfToken(response);
        const customerDetails = {
          customerId: response['response']['userDetails']['customerId'],
          customerName: response['response']['userDetails']['customerName']
        };
        if (this.isBrowser) {
          localStorage.setItem('customerInfo', JSON.stringify(customerDetails));
        }
        this.dataSrv.setLoggedInUserInfo(response);
      }
      return true;
    }
    return false;
  }

  private setCsrfToken(csrfTokenResponse: any): void {
    const { response } = csrfTokenResponse;
    if (!isNullOrUndefined(response)) {
      const { tokens } = response;
      if (!isNullOrUndefined(tokens)) {
        const { csrf } = tokens;
        if (this.isBrowser && typeof localStorage !== 'undefined' && isNullOrUndefined(localStorage.getItem('csrfToken'))) {
          localStorage.setItem('csrfToken', csrf);
        }
      }
    }
  }

  private loadUserPermissions(userId: any, resource: string, isLanding: boolean = false): Promise<boolean | UrlTree> {
    this.appStateSrv.setInProcess(true);
    const options = { userId };


    return new Promise((resolve, reject) => {
      this.httpSrv.makePostApiCall('GET_USER_PERMISSIONS', options, this.envService.baseUrl).subscribe({
        next: (res: any) => {
          resolve(this.handleUserPermissionsResponse(res, resource, isLanding));
        },
        error: () => {
          resolve(this.handleUnAuthorizedAccess());
        }
      });
    });
  }

  private handleUserPermissionsResponse(res: any, resource: string, isLanding: boolean = false): boolean | UrlTree {
    if (!isNullOrUndefined(res.response)) {
      this.userPermissions = res.response?.permissions ? res.response.permissions : {};
      const metaInfo = res.response?.meta ? res.response.meta : {};

      this.appStateSrv.setLoggedInUserType(res.response);
      this.appStateSrv.setUserMetaInfo(metaInfo);
      this.appStateSrv.setInProcess(false);
      this.appStateSrv.setPermissionsLoaded(true);
      this.appStateSrv.setUserPermissionsRef(this.userPermissions);

      localStorage.setItem('userPermissions', JSON.stringify(this.userPermissions));
      localStorage.setItem('metaInfo', JSON.stringify(metaInfo));

      return this.handlePermissionsLoaded(resource, isLanding);
    } else {
      return this.handleUnAuthorizedAccess();
    }
  }

  handleUnAuthorizedAccess(message: string = "You don't have permissions to view the requested resource", route: string[] = ['', 'login']) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
    this.router.navigate(['', 'access-denied']);
    return false;
  }

  checkPermissions(resource: string): boolean | UrlTree {
    if (resource === PROJECT_CONSTANTS.USERMGMT_APP_NAME) {
      return hasApplicationAccess(this.userPermissions, PROJECT_CONSTANTS.USERMGMT_APP_NAME)
        ? true
        : this.handleUnAuthorizedAccess();
    }

    if (resource) {
      const appName = PROJECT_CONSTANTS.APPLICATION_NAME;
      const permissions = parseUserPermissions(this.userPermissions);

      if (permissions[appName]?.resources?.[resource]) {
        return true;
      }

      const firstAvailableResource = this.getFirstAvailableResource();

      if (firstAvailableResource) {
        const route = RESOURCE_ROUTE_MAP[firstAvailableResource];
        if (route) {
          this.router.navigate([route]);
          return this.router.parseUrl(route);
        }
      }

      return this.handleUnAuthorizedAccess();
    }
    return false;
  }

  private getFirstAvailableResource(): string | null {
    const appName = PROJECT_CONSTANTS.APPLICATION_NAME;
    const permissions = parseUserPermissions(this.userPermissions);
    const resources = permissions?.[appName]?.resources;

    if (resources) {
      const dashboardResources: any = Object.values(resources)
        .filter((res: any) => res?.resourceTypeName === "Dashboards" && res?.resourceName != 'Bottombar');

      if (dashboardResources.length > 0) {
        return dashboardResources[0].resourceName;
      }
    }

    return null;
  }

}